import { useState, useRef, useCallback, useEffect } from 'react';
import { useAuth } from '../lib/auth-context';
import { createApiClient } from '../lib/api';
import {
  createScanSession,
  uploadImage,
  uploadManifest,
  getScan,
  listScanMessages,
  type ScanMessage,
  type ScanImage,
} from '../lib/appsync';

type Phase = 'idle' | 'compressing' | 'uploading' | 'processing' | 'complete' | 'failed' | 'needs_followup';

interface PipelineStage {
  name: string;
  status: 'waiting' | 'running' | 'done' | 'error';
  startedAt?: number;
  durationMs?: number;
  detail?: string;
  data?: any;
}

interface ImagePreview {
  file: File;
  preview: string;
  compressedFile?: File;
  originalSize?: number;
  compressedSize?: number;
  uploadProgress?: 'pending' | 'uploading' | 'done' | 'error';
}

const MAX_DIM = 1024;
const JPEG_QUALITY = 0.80;
const JPEG_QUALITY_FALLBACK = 0.60;
const MAX_BYTES = 500_000; // 500KB target
const PIPELINE_TIMEOUT_MS = 5 * 60 * 1000; // 5 minutes max before we consider pipeline stalled

const CATEGORY_OPTIONS = [
  '',
  'watches',
  'jewelry',
  'handbags',
  'sneakers',
  'electronics',
  'art',
  'collectibles',
  'furniture',
  'clothing',
  'sports',
  'toys',
  'coins',
  'stamps',
  'musical_instruments',
  'tools',
  'vintage',
  'antiques',
];

/** Compress an image using Canvas API (browser equivalent of mobile's expo-image-manipulator) */
async function compressImage(file: File): Promise<{ compressed: File; originalSize: number; compressedSize: number }> {
  const originalSize = file.size;

  // Load image into an Image element
  const img = await new Promise<HTMLImageElement>((resolve, reject) => {
    const i = new Image();
    i.onload = () => resolve(i);
    i.onerror = reject;
    i.src = URL.createObjectURL(file);
  });

  // Calculate resize dimensions (max 1024px on longest side)
  let { width, height } = img;
  if (width > MAX_DIM || height > MAX_DIM) {
    if (width > height) {
      height = Math.round((height / width) * MAX_DIM);
      width = MAX_DIM;
    } else {
      width = Math.round((width / height) * MAX_DIM);
      height = MAX_DIM;
    }
  }

  // Draw to canvas
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d')!;
  ctx.drawImage(img, 0, 0, width, height);
  URL.revokeObjectURL(img.src);

  // Compress to JPEG
  let blob = await new Promise<Blob>((resolve) =>
    canvas.toBlob((b) => resolve(b!), 'image/jpeg', JPEG_QUALITY)
  );

  // If still too large, compress harder
  if (blob.size > MAX_BYTES) {
    blob = await new Promise<Blob>((resolve) =>
      canvas.toBlob((b) => resolve(b!), 'image/jpeg', JPEG_QUALITY_FALLBACK)
    );
  }

  const compressed = new File([blob], file.name.replace(/\.[^.]+$/, '.jpg'), { type: 'image/jpeg' });
  return { compressed, originalSize, compressedSize: compressed.size };
}

export function DebugTestPage() {
  const { idToken, getValidToken } = useAuth();
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Image state
  const [images, setImages] = useState<ImagePreview[]>([]);
  const [categoryHint, setCategoryHint] = useState('');
  const [dragOver, setDragOver] = useState(false);

  // Pipeline state
  const [phase, setPhase] = useState<Phase>('idle');
  const [scanSessionId, setScanSessionId] = useState<string | null>(null);
  const [stages, setStages] = useState<PipelineStage[]>([]);
  const [messages, setMessages] = useState<ScanMessage[]>([]);
  const [scanImages, setScanImages] = useState<ScanImage[]>([]);
  const [fastResult, setFastResult] = useState<any>(null);
  const [deepResult, setDeepResult] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [pipelineStartTime, setPipelineStartTime] = useState<number | null>(null);
  const [totalDurationMs, setTotalDurationMs] = useState<number | null>(null);

  // Comp review state: compUrl -> relevance
  const [compReviews, setCompReviews] = useState<Record<string, 'relevant' | 'irrelevant' | 'marginal'>>({});
  const [reviewingComp, setReviewingComp] = useState<string | null>(null);

  // Polling ref
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const lastSeqRef = useRef(0);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (pollingRef.current) clearInterval(pollingRef.current);
    };
  }, []);

  const addImages = useCallback((files: FileList | File[]) => {
    const newImages: ImagePreview[] = [];
    const fileArray = Array.from(files);
    for (const file of fileArray) {
      if (!file.type.startsWith('image/')) continue;
      if (images.length + newImages.length >= 5) break;
      newImages.push({
        file,
        preview: URL.createObjectURL(file),
        originalSize: file.size,
      });
    }
    setImages((prev) => [...prev, ...newImages].slice(0, 5));
  }, [images.length]);

  // Paste images from clipboard
  useEffect(() => {
    if (phase !== 'idle') return;
    const handlePaste = (e: ClipboardEvent) => {
      const items = e.clipboardData?.items;
      if (!items) return;
      const files: File[] = [];
      for (const item of Array.from(items)) {
        if (item.type.startsWith('image/')) {
          const file = item.getAsFile();
          if (file) files.push(file);
        }
      }
      if (files.length > 0) {
        e.preventDefault();
        addImages(files);
      }
    };
    document.addEventListener('paste', handlePaste);
    return () => document.removeEventListener('paste', handlePaste);
  }, [phase, addImages]);

  const removeImage = useCallback((index: number) => {
    setImages((prev) => {
      const removed = prev[index];
      if (removed) URL.revokeObjectURL(removed.preview);
      return prev.filter((_, i) => i !== index);
    });
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    addImages(e.dataTransfer.files);
  }, [addImages]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(true);
  }, []);

  const handleDragLeave = useCallback(() => setDragOver(false), []);

  const updateStage = useCallback((name: string, update: Partial<PipelineStage>) => {
    setStages((prev) => prev.map((s) => (s.name === name ? { ...s, ...update } : s)));
  }, []);

  // Start the analysis pipeline
  const startAnalysis = useCallback(async () => {
    if (images.length === 0) return;

    setError(null);
    setFastResult(null);
    setDeepResult(null);
    setMessages([]);
    setScanImages([]);
    setTotalDurationMs(null);
    lastSeqRef.current = 0;
    const startTime = Date.now();
    setPipelineStartTime(startTime);

    const initialStages: PipelineStage[] = [
      { name: 'CompressImages', status: 'running', startedAt: startTime },
      { name: 'CreateSession', status: 'waiting' },
      { name: 'UploadImages', status: 'waiting' },
      { name: 'UploadManifest', status: 'waiting' },
      { name: 'ValidateAndLock', status: 'waiting' },
      { name: 'NormalizeImages', status: 'waiting' },
      { name: 'FastIdentify', status: 'waiting' },
      { name: 'DeepAuthenticity', status: 'waiting' },
      { name: 'DeepValuation', status: 'waiting' },
      { name: 'Finalize', status: 'waiting' },
    ];
    setStages(initialStages);
    setPhase('compressing');

    try {
      // 0. Compress images client-side (like mobile app does)
      const compressResults = await Promise.all(
        images.map((img) => compressImage(img.file))
      );

      setImages((prev) =>
        prev.map((img, i) => ({
          ...img,
          compressedFile: compressResults[i].compressed,
          originalSize: compressResults[i].originalSize,
          compressedSize: compressResults[i].compressedSize,
        }))
      );

      const totalOriginal = compressResults.reduce((s, r) => s + r.originalSize, 0);
      const totalCompressed = compressResults.reduce((s, r) => s + r.compressedSize, 0);

      updateStage('CompressImages', {
        status: 'done',
        durationMs: Date.now() - startTime,
        detail: `${images.length} images: ${formatBytes(totalOriginal)} -> ${formatBytes(totalCompressed)} (${MAX_DIM}px, JPEG ${Math.round(JPEG_QUALITY * 100)}%)`,
      });

      // 1. Create scan session via AppSync
      setPhase('uploading');
      const createStart = Date.now();
      updateStage('CreateSession', { status: 'running', startedAt: createStart });

      const token = await getValidToken();
      const session = await createScanSession(
        token,
        images.length,
        categoryHint || undefined,
      );
      const sessionId = session.scanSessionId;
      setScanSessionId(sessionId);

      updateStage('CreateSession', {
        status: 'done',
        durationMs: Date.now() - createStart,
        detail: `Session: ${sessionId.slice(0, 12)}...`,
        data: { scanSessionId: sessionId, uploadTargets: session.uploadTargets.length },
      });

      // 2. Upload compressed images in parallel
      const uploadStart = Date.now();
      updateStage('UploadImages', { status: 'running', startedAt: uploadStart });

      setImages((prev) =>
        prev.map((img) => ({ ...img, uploadProgress: 'uploading' as const }))
      );

      await Promise.all(
        session.uploadTargets.map(async (target, i) => {
          const compressed = compressResults[i]?.compressed;
          if (!compressed) return;
          await uploadImage(target.presignedUrl, compressed);
          setImages((prev) =>
            prev.map((im, idx) =>
              idx === i ? { ...im, uploadProgress: 'done' as const } : im
            )
          );
        })
      );

      updateStage('UploadImages', {
        status: 'done',
        durationMs: Date.now() - uploadStart,
        detail: `${images.length} image(s) uploaded (${formatBytes(totalCompressed)})`,
      });

      // 3. Upload manifest (triggers pipeline)
      const manifestStart = Date.now();
      updateStage('UploadManifest', { status: 'running', startedAt: manifestStart });

      const manifest = {
        scanSessionId: sessionId,
        expectedImageCount: images.length,
        images: session.uploadTargets.map((t, i) => ({
          s3Key: t.s3Key,
          captureOrder: i,
          contentType: 'image/jpeg',
        })),
        clientHints: categoryHint ? { categoryHint } : undefined,
      };

      await uploadManifest(session.manifestUploadTarget.presignedUrl, manifest);

      updateStage('UploadManifest', {
        status: 'done',
        durationMs: Date.now() - manifestStart,
        detail: 'Pipeline triggered',
      });

      // 4. Start polling for pipeline progress
      setPhase('processing');
      updateStage('ValidateAndLock', { status: 'running', startedAt: Date.now() });

      startPolling(sessionId, startTime);
    } catch (err: any) {
      setPhase('failed');
      setError(err.message || 'Failed to start analysis');
    }
  }, [images, categoryHint, getValidToken, updateStage]);

  const startPolling = useCallback(
    (sessionId: string, startTime: number) => {
      if (pollingRef.current) clearInterval(pollingRef.current);

      const poll = async () => {
        try {
          // Get a fresh token each poll (auto-refreshes if near expiry)
          const token = await getValidToken();

          // Poll both scan status and messages
          const [scanResult, msgResult] = await Promise.all([
            getScan(token, sessionId),
            listScanMessages(token, sessionId, lastSeqRef.current),
          ]);

          const { session, latestFast, latestDeep, images: scanImgs } = scanResult;

          // Update images from scan
          if (scanImgs && scanImgs.length > 0) {
            setScanImages(scanImgs);
          }

          // Process new messages
          if (msgResult.messages.length > 0) {
            setMessages((prev) => {
              const existing = new Set(prev.map((m) => m.messageId));
              const newMsgs = msgResult.messages.filter((m) => !existing.has(m.messageId));
              return [...prev, ...newMsgs].sort((a, b) => a.seq - b.seq);
            });
            lastSeqRef.current = msgResult.lastSeq ?? lastSeqRef.current;

            // Update pipeline stages based on messages
            for (const msg of msgResult.messages) {
              updatePipelineFromMessage(msg);
            }
          }

          // Update results
          if (latestFast) {
            const fast = typeof latestFast === 'string' ? JSON.parse(latestFast) : latestFast;
            setFastResult(fast);
          }
          if (latestDeep) {
            const deep = typeof latestDeep === 'string' ? JSON.parse(latestDeep) : latestDeep;
            setDeepResult(deep);
          }

          // Check terminal states
          const terminalStatuses = ['COMPLETE', 'FAILED', 'NEEDS_FOLLOWUP'];
          if (terminalStatuses.includes(session.status)) {
            const phaseMap: Record<string, Phase> = {
              COMPLETE: 'complete',
              FAILED: 'failed',
              NEEDS_FOLLOWUP: 'needs_followup',
            };
            setPhase(phaseMap[session.status] || 'failed');
            setTotalDurationMs(Date.now() - startTime);
            if (pollingRef.current) {
              clearInterval(pollingRef.current);
              pollingRef.current = null;
            }
          } else if (Date.now() - startTime > PIPELINE_TIMEOUT_MS) {
            // Pipeline appears stalled — stop polling
            setPhase('failed');
            setError(`Pipeline timed out after ${Math.round(PIPELINE_TIMEOUT_MS / 1000)}s. Last status: ${session.status}`);
            setTotalDurationMs(Date.now() - startTime);
            if (pollingRef.current) {
              clearInterval(pollingRef.current);
              pollingRef.current = null;
            }
          }
        } catch (err: any) {
          console.error('Polling error:', err);
        }
      };

      // Poll immediately, then every 2 seconds
      poll();
      pollingRef.current = setInterval(poll, 2000);
    },
    [getValidToken]
  );

  const updatePipelineFromMessage = useCallback(
    (msg: ScanMessage) => {
      const now = Date.now();
      const payload = typeof msg.payload === 'string' ? tryParse(msg.payload) : msg.payload;

      setStages((prev) => {
        const stages = [...prev];
        const findStage = (name: string) => stages.find((s) => s.name === name);

        switch (msg.seq) {
          case 1: {
            // ValidateAndLock done
            const stage = findStage('ValidateAndLock');
            if (stage && stage.status !== 'done') {
              stage.status = 'done';
              stage.durationMs = stage.startedAt ? now - stage.startedAt : undefined;
              stage.detail = 'Session locked, pipeline started';
            }
            const normStage = findStage('NormalizeImages');
            if (normStage && normStage.status === 'waiting') {
              normStage.status = 'running';
              normStage.startedAt = now;
            }
            break;
          }
          case 3: {
            // FastIdentify done (PARTIAL_RESULT)
            const normStage = findStage('NormalizeImages');
            if (normStage && normStage.status !== 'done') {
              normStage.status = 'done';
              normStage.durationMs = normStage.startedAt ? now - normStage.startedAt : undefined;
            }
            const fastStage = findStage('FastIdentify');
            if (fastStage) {
              fastStage.status = 'done';
              fastStage.durationMs = fastStage.startedAt ? now - fastStage.startedAt : undefined;
              if (payload?.candidates?.[0]) {
                const c = payload.candidates[0];
                fastStage.detail = `${[c.brand, c.model, c.variant].filter(Boolean).join(' ')} (${(c.confidence * 100).toFixed(0)}%)`;
              }
              fastStage.data = payload;
            }
            // Start deep authenticity
            const authStage = findStage('DeepAuthenticity');
            if (authStage && authStage.status === 'waiting') {
              authStage.status = 'running';
              authStage.startedAt = now;
            }
            break;
          }
          case 4: {
            const fastStage = findStage('FastIdentify');
            if (fastStage) {
              fastStage.detail = 'Low confidence - needs followup';
              fastStage.data = payload;
            }
            break;
          }
          case 6: {
            // DeepAuthenticity done
            const authStage = findStage('DeepAuthenticity');
            if (authStage) {
              authStage.status = 'done';
              authStage.durationMs = authStage.startedAt ? now - authStage.startedAt : undefined;
              if (payload?.authConfidence != null) {
                authStage.detail = `Auth confidence: ${(payload.authConfidence * 100).toFixed(0)}%`;
              }
              authStage.data = payload;
            }
            const valStage = findStage('DeepValuation');
            if (valStage && valStage.status === 'waiting') {
              valStage.status = 'running';
              valStage.startedAt = now;
            }
            break;
          }
          case 7: {
            // DeepValuation done
            const valStage = findStage('DeepValuation');
            if (valStage) {
              valStage.status = 'done';
              valStage.durationMs = valStage.startedAt ? now - valStage.startedAt : undefined;
              const valData = payload?.valuation || payload;
              if (valData?.comps) {
                valStage.detail = `${valData.comps.length} comps found`;
              }
              valStage.data = payload;
            }
            const finStage = findStage('Finalize');
            if (finStage && finStage.status === 'waiting') {
              finStage.status = 'running';
              finStage.startedAt = now;
            }
            break;
          }
          case 8: {
            // Finalize done
            const finStage = findStage('Finalize');
            if (finStage) {
              finStage.status = 'done';
              finStage.durationMs = finStage.startedAt ? now - finStage.startedAt : undefined;
              finStage.detail = 'Pipeline complete';
              finStage.data = payload;
            }
            break;
          }
        }

        return stages;
      });
    },
    []
  );

  // Auto-advance FastIdentify to running when NormalizeImages is done
  useEffect(() => {
    const normStage = stages.find((s) => s.name === 'NormalizeImages');
    const fastStage = stages.find((s) => s.name === 'FastIdentify');
    if (normStage?.status === 'done' && fastStage?.status === 'waiting') {
      updateStage('FastIdentify', { status: 'running', startedAt: Date.now() });
    }
  }, [stages, updateStage]);

  const submitCompReview = useCallback(async (
    comp: any,
    relevance: 'relevant' | 'irrelevant' | 'marginal',
  ) => {
    if (!scanSessionId || !comp.url) return;
    const compUrl = comp.url;
    setReviewingComp(compUrl);
    try {
      const token = await getValidToken();
      const api = createApiClient(token);
      const candidate = fastResult?.candidates?.[0];
      await api.submitCompReview({
        scanSessionId,
        compUrl,
        relevance,
        candidateBrand: candidate?.brand,
        candidateModel: candidate?.model,
        candidateCategory: fastResult?.resolvedCategory,
        compTitle: comp.title,
        compPrice: comp.price,
        compSource: comp.source,
        compPriceType: comp.priceType,
        relevanceScore: comp.relevanceScore,
      });
      setCompReviews((prev) => ({ ...prev, [compUrl]: relevance }));
    } catch (err: any) {
      console.error('Failed to submit comp review:', err);
    } finally {
      setReviewingComp(null);
    }
  }, [scanSessionId, fastResult, getValidToken]);

  const reset = useCallback(() => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
    // Revoke object URLs to free memory
    images.forEach((img) => URL.revokeObjectURL(img.preview));
    setImages([]);
    setCategoryHint('');
    setPhase('idle');
    setScanSessionId(null);
    setStages([]);
    setMessages([]);
    setScanImages([]);
    setFastResult(null);
    setDeepResult(null);
    setError(null);
    setPipelineStartTime(null);
    setTotalDurationMs(null);
    setCompReviews({});
    lastSeqRef.current = 0;
  }, [images]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold text-white">Debug Test Page</h1>
        {phase !== 'idle' && (
          <button
            onClick={reset}
            className="px-4 py-1.5 text-sm bg-slate-800 text-slate-300 rounded hover:bg-slate-700"
          >
            Reset
          </button>
        )}
      </div>

      {/* Upload Area */}
      {phase === 'idle' && (
        <div className="space-y-4">
          <div
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onClick={() => fileInputRef.current?.click()}
            className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
              dragOver
                ? 'border-orange-500 bg-orange-950/20'
                : 'border-slate-700 hover:border-slate-500 bg-slate-900'
            }`}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple
              className="hidden"
              onChange={(e) => e.target.files && addImages(e.target.files)}
            />
            <div className="text-slate-400 text-sm">
              {images.length === 0
                ? 'Drop photos here, paste from clipboard, or click to select (max 5)'
                : `${images.length}/5 photos selected - drop, paste, or click to add more`}
            </div>
            <div className="text-slate-600 text-xs mt-1">
              Images will be resized to {MAX_DIM}px and compressed to JPEG before upload
            </div>
          </div>

          {images.length > 0 && (
            <div className="flex gap-3 flex-wrap">
              {images.map((img, i) => (
                <div key={i} className="relative group">
                  <img
                    src={img.preview}
                    alt={`Photo ${i + 1}`}
                    className="h-24 w-24 object-cover rounded border border-slate-700"
                  />
                  <button
                    onClick={() => removeImage(i)}
                    className="absolute -top-2 -right-2 w-5 h-5 bg-red-600 text-white text-xs rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    x
                  </button>
                  <div className="text-xs text-slate-600 text-center mt-1">
                    {formatBytes(img.originalSize || img.file.size)}
                  </div>
                  {img.uploadProgress === 'done' && (
                    <div className="absolute bottom-6 left-0 right-0 bg-green-600/80 text-white text-xs text-center py-0.5 rounded-b">
                      Uploaded
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          <div className="flex items-center gap-4">
            <label className="text-sm text-slate-400">Category hint:</label>
            <select
              value={categoryHint}
              onChange={(e) => setCategoryHint(e.target.value)}
              className="bg-slate-800 text-slate-300 text-sm rounded px-3 py-1.5 border border-slate-700 focus:border-orange-500 outline-none"
            >
              {CATEGORY_OPTIONS.map((cat) => (
                <option key={cat} value={cat}>
                  {cat || '(none)'}
                </option>
              ))}
            </select>

            <button
              onClick={startAnalysis}
              disabled={images.length === 0}
              className="px-6 py-2 bg-orange-600 text-white font-medium rounded hover:bg-orange-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors ml-auto"
            >
              Start Analysis
            </button>
          </div>
        </div>
      )}

      {/* Pipeline Progress */}
      {phase !== 'idle' && (
        <div className="bg-slate-900 border border-slate-800 rounded-lg p-4">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-sm font-medium text-slate-400">Pipeline Progress</h2>
            <div className="flex items-center gap-3">
              {scanSessionId && (
                <span className="text-xs text-slate-600 font-mono">{scanSessionId.slice(0, 12)}...</span>
              )}
              {totalDurationMs != null && (
                <span className="text-xs text-slate-500">Total: {(totalDurationMs / 1000).toFixed(1)}s</span>
              )}
              {(phase === 'processing' || phase === 'compressing' || phase === 'uploading') && pipelineStartTime && (
                <ElapsedTimer startTime={pipelineStartTime} />
              )}
              <PhaseIndicator phase={phase} />
            </div>
          </div>

          <div className="space-y-2">
            {stages.map((stage) => (
              <StageRow key={stage.name} stage={stage} />
            ))}
          </div>

          {error && (
            <div className="mt-4 px-3 py-2 bg-red-950/50 border border-red-800 rounded text-sm text-red-400">
              {error}
            </div>
          )}
        </div>
      )}

      {/* Uploaded Images (from scan) */}
      {scanImages.length > 0 && (
        <section className="bg-slate-900 border border-slate-800 rounded-lg p-4">
          <h2 className="text-sm font-medium text-slate-400 mb-3">Processed Images</h2>
          <div className="flex gap-3 overflow-x-auto pb-2">
            {scanImages.map((img) => (
              <a key={img.imageId} href={img.url} target="_blank" rel="noopener noreferrer" className="shrink-0">
                <img
                  src={img.thumbnailUrl || img.url}
                  alt={`Capture ${img.captureOrder}`}
                  className="h-32 w-32 object-cover rounded border border-slate-700 hover:border-orange-500 transition-colors"
                />
              </a>
            ))}
          </div>
        </section>
      )}

      {/* Fast Identify Results */}
      {fastResult && (
        <section className="bg-slate-900 border border-slate-800 rounded-lg p-4">
          <h2 className="text-sm font-medium text-slate-400 mb-3">Fast Identify Result</h2>
          {fastResult.candidates?.map((candidate: any, i: number) => (
            <div key={i} className="mb-4 last:mb-0">
              <div className="flex items-center gap-3 mb-2">
                <span className="text-white font-medium">
                  {[candidate.brand, candidate.model, candidate.variant].filter(Boolean).join(' ')}
                </span>
                <span className="px-2 py-0.5 bg-orange-900/50 text-orange-300 text-xs rounded">
                  {(candidate.confidence * 100).toFixed(0)}%
                </span>
                {candidate.era && <span className="text-sm text-slate-500">{candidate.era}</span>}
              </div>
              {candidate.evidence && (
                <ul className="text-sm text-slate-400 space-y-1 ml-4">
                  {candidate.evidence.map((e: string, j: number) => (
                    <li key={j}>- {e}</li>
                  ))}
                </ul>
              )}
            </div>
          ))}
          {fastResult.resolvedCategory && (
            <div className="text-sm text-slate-500 mt-2">
              Resolved category: <span className="text-slate-300">{fastResult.resolvedCategory}</span>
            </div>
          )}
          {fastResult.followupQuestions && fastResult.followupQuestions.length > 0 && (
            <div className="mt-3">
              <div className="text-xs text-slate-500 mb-1">Followup Questions:</div>
              {fastResult.followupQuestions.map((q: any, i: number) => (
                <div key={i} className="text-sm text-amber-400 ml-2">
                  - {typeof q === 'string' ? q : q.question || JSON.stringify(q)}
                </div>
              ))}
            </div>
          )}
          <CollapsibleJson title="Raw Fast Result" data={fastResult} />
        </section>
      )}

      {/* Deep Authenticity Results */}
      {deepResult?.authenticity && (
        <section className="bg-slate-900 border border-slate-800 rounded-lg p-4">
          <h2 className="text-sm font-medium text-slate-400 mb-3">
            Authenticity
            {deepResult.authenticity.authConfidence != null && (
              <span className="ml-2 px-2 py-0.5 bg-orange-900/50 text-orange-300 text-xs rounded">
                {(deepResult.authenticity.authConfidence * 100).toFixed(0)}%
              </span>
            )}
          </h2>
          <div className="space-y-2">
            {(deepResult.authenticity.indicators?.length ? deepResult.authenticity.indicators : deepResult.authenticity.watchFor || []).map((ind: any, i: number) => (
              <div key={i} className="flex items-start gap-2 text-sm">
                <span
                  className={`shrink-0 mt-0.5 w-2 h-2 rounded-full ${
                    typeof ind === 'string' ? 'bg-slate-400' :
                    ind.status === 'pass' ? 'bg-green-500' :
                    ind.status === 'warning' ? 'bg-amber-500' :
                    ind.status === 'fail' ? 'bg-red-500' : 'bg-slate-500'
                  }`}
                />
                <div>
                  {typeof ind === 'string' ? (
                    <span className="text-white">{ind}</span>
                  ) : (
                    <>
                      <span className="text-white">{ind.indicator || ind.area}</span>
                      <span className="text-slate-500 ml-2">{ind.detail || ind.description}</span>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
          <CollapsibleJson title="Raw Authenticity Result" data={deepResult.authenticity} />
        </section>
      )}

      {/* Deep Valuation Results */}
      {deepResult?.valuation && (
        <section className="bg-slate-900 border border-slate-800 rounded-lg p-4">
          <h2 className="text-sm font-medium text-slate-400 mb-3">Valuation</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm mb-4">
            {deepResult.valuation.resaleRange && (
              <div>
                <div className="text-slate-500">Resale Range</div>
                <div className="text-white font-medium">
                  ${deepResult.valuation.resaleRange.min} - ${deepResult.valuation.resaleRange.max}
                </div>
              </div>
            )}
            {deepResult.valuation.expectedPayRange && (
              <div>
                <div className="text-slate-500">Expected Pay</div>
                <div className="text-white">
                  ${deepResult.valuation.expectedPayRange.min} - ${deepResult.valuation.expectedPayRange.max}
                </div>
              </div>
            )}
            {deepResult.valuation.liquidityScore != null && (
              <div>
                <div className="text-slate-500">Liquidity</div>
                <div className="text-white">{(deepResult.valuation.liquidityScore * 100).toFixed(0)}%</div>
              </div>
            )}
            {deepResult.valuation.pricingConfidence != null && (
              <div>
                <div className="text-slate-500">Pricing Confidence</div>
                <div className="text-white">{(deepResult.valuation.pricingConfidence * 100).toFixed(0)}%</div>
              </div>
            )}
          </div>

          {deepResult.valuation.searchTerms && (
            <div className="mb-4">
              <div className="text-xs text-slate-500 mb-1">Search Queries</div>
              <div className="flex flex-wrap gap-2">
                {deepResult.valuation.searchTerms.map((term: string, i: number) => (
                  <span key={i} className="px-2 py-0.5 bg-slate-800 text-slate-300 text-xs rounded">{term}</span>
                ))}
              </div>
            </div>
          )}

          {deepResult.valuation.comps && deepResult.valuation.comps.length > 0 && (
            <div>
              <div className="flex items-center gap-3 mb-2">
                <div className="text-xs text-slate-500">
                  Comparables ({deepResult.valuation.comps.length})
                </div>
                {Object.keys(compReviews).length > 0 && (
                  <div className="text-xs text-slate-600">
                    Reviewed: {Object.keys(compReviews).length}/{deepResult.valuation.comps.length}
                  </div>
                )}
              </div>
              <div className="space-y-1">
                {deepResult.valuation.comps.map((comp: any, i: number) => {
                  const review = comp.url ? compReviews[comp.url] : undefined;
                  const isReviewing = comp.url === reviewingComp;
                  return (
                    <div
                      key={i}
                      className={`flex items-center gap-3 text-sm rounded px-3 py-2 ${
                        review === 'relevant' ? 'border border-green-800/50 bg-green-950/20' :
                        review === 'irrelevant' ? 'border border-red-800/50 bg-red-950/20 opacity-60' :
                        review === 'marginal' ? 'border border-amber-800/50 bg-amber-950/20' :
                        'border border-slate-800/50'
                      }`}
                    >
                      {/* Review buttons */}
                      <div className="flex flex-col gap-0.5 shrink-0">
                        <button
                          onClick={() => submitCompReview(comp, 'relevant')}
                          disabled={isReviewing}
                          className={`w-7 h-7 rounded text-xs flex items-center justify-center transition-colors ${
                            review === 'relevant'
                              ? 'bg-green-600 text-white'
                              : 'bg-slate-800 text-slate-400 hover:bg-green-900 hover:text-green-300'
                          }`}
                          title="Relevant"
                        >
                          Y
                        </button>
                        <button
                          onClick={() => submitCompReview(comp, 'marginal')}
                          disabled={isReviewing}
                          className={`w-7 h-7 rounded text-xs flex items-center justify-center transition-colors ${
                            review === 'marginal'
                              ? 'bg-amber-600 text-white'
                              : 'bg-slate-800 text-slate-400 hover:bg-amber-900 hover:text-amber-300'
                          }`}
                          title="Marginal"
                        >
                          ~
                        </button>
                        <button
                          onClick={() => submitCompReview(comp, 'irrelevant')}
                          disabled={isReviewing}
                          className={`w-7 h-7 rounded text-xs flex items-center justify-center transition-colors ${
                            review === 'irrelevant'
                              ? 'bg-red-600 text-white'
                              : 'bg-slate-800 text-slate-400 hover:bg-red-900 hover:text-red-300'
                          }`}
                          title="Not relevant"
                        >
                          N
                        </button>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-slate-300 truncate">
                          {comp.url ? (
                            <a href={comp.url} target="_blank" rel="noopener noreferrer" className="hover:text-orange-400">
                              {comp.title}
                            </a>
                          ) : comp.title}
                        </div>
                        <div className="flex gap-2 mt-0.5">
                          <span className="text-xs text-slate-500">{comp.source}</span>
                          <span className={`text-xs ${comp.priceType === 'sold' ? 'text-green-400' : 'text-blue-400'}`}>
                            {comp.priceType || 'asking'}
                          </span>
                          {comp.condition && <span className="text-xs text-slate-500">{comp.condition}</span>}
                          {comp.relevanceScore != null && (
                            <span className="text-xs text-slate-600">
                              relevance: {(comp.relevanceScore * 100).toFixed(0)}%
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="text-white font-medium ml-4 shrink-0">${comp.price}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          <CollapsibleJson title="Raw Valuation Result" data={deepResult.valuation} />
        </section>
      )}

      {/* Pipeline Messages Timeline */}
      {messages.length > 0 && (
        <section className="bg-slate-900 border border-slate-800 rounded-lg p-4">
          <h2 className="text-sm font-medium text-slate-400 mb-3">Pipeline Messages ({messages.length})</h2>
          <div className="space-y-2">
            {messages.map((msg) => {
              const payload = typeof msg.payload === 'string' ? tryParse(msg.payload) : msg.payload;
              return (
                <div key={msg.messageId} className="text-sm border-l-2 border-slate-700 pl-3">
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-slate-500">#{msg.seq}</span>
                    <span className="text-xs px-1.5 py-0.5 bg-slate-800 rounded text-slate-400">{msg.type}</span>
                    <span className="text-xs text-slate-600">
                      {new Date(msg.createdAt).toLocaleTimeString()}
                    </span>
                  </div>
                  <CollapsibleJson title="Payload" data={payload} />
                </div>
              );
            })}
          </div>
        </section>
      )}

      {phase === 'complete' && scanSessionId && (
        <div className="text-center">
          <a
            href={`/scans/${scanSessionId}`}
            className="text-sm text-orange-400 hover:text-orange-300"
          >
            View full scan detail with comp review tools &rarr;
          </a>
        </div>
      )}
    </div>
  );
}

// Helper components

function StageRow({ stage }: { stage: PipelineStage }) {
  const statusIcon =
    stage.status === 'done' ? '\u2705' :
    stage.status === 'running' ? '\u23F3' :
    stage.status === 'error' ? '\u274C' : '\u23F8';

  return (
    <div
      className={`flex items-center gap-3 px-3 py-2 rounded text-sm ${
        stage.status === 'running'
          ? 'bg-blue-950/30 border border-blue-800/50'
          : stage.status === 'done'
          ? 'bg-slate-800/30'
          : stage.status === 'error'
          ? 'bg-red-950/30 border border-red-800/50'
          : 'opacity-50'
      }`}
    >
      <span className="text-base">{statusIcon}</span>
      <span className={`font-medium min-w-[160px] ${stage.status === 'running' ? 'text-blue-300' : 'text-slate-300'}`}>
        {stage.name}
      </span>
      {stage.durationMs != null && (
        <span className="text-xs text-slate-500">{(stage.durationMs / 1000).toFixed(1)}s</span>
      )}
      {stage.status === 'running' && stage.startedAt && (
        <ElapsedTimer startTime={stage.startedAt} />
      )}
      {stage.detail && <span className="text-xs text-slate-400 ml-auto truncate max-w-[400px]">{stage.detail}</span>}
    </div>
  );
}

function PhaseIndicator({ phase }: { phase: Phase }) {
  const colors: Record<Phase, string> = {
    idle: 'bg-slate-700 text-slate-400',
    compressing: 'bg-blue-900/50 text-blue-300',
    uploading: 'bg-blue-900/50 text-blue-300',
    processing: 'bg-blue-900/50 text-blue-300 animate-pulse',
    complete: 'bg-green-900/50 text-green-300',
    failed: 'bg-red-900/50 text-red-300',
    needs_followup: 'bg-amber-900/50 text-amber-300',
  };

  const labels: Record<Phase, string> = {
    idle: 'Idle',
    compressing: 'Compressing...',
    uploading: 'Uploading...',
    processing: 'Processing...',
    complete: 'Complete',
    failed: 'Failed',
    needs_followup: 'Needs Followup',
  };

  return (
    <span className={`px-2 py-0.5 text-xs rounded ${colors[phase]}`}>
      {labels[phase]}
    </span>
  );
}

function ElapsedTimer({ startTime }: { startTime: number }) {
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setElapsed(Date.now() - startTime);
    }, 100);
    return () => clearInterval(interval);
  }, [startTime]);

  return (
    <span className="text-xs text-blue-400 tabular-nums">{(elapsed / 1000).toFixed(1)}s</span>
  );
}

function CollapsibleJson({ title, data }: { title: string; data: any }) {
  const [open, setOpen] = useState(false);

  return (
    <div className="mt-2">
      <button
        onClick={() => setOpen(!open)}
        className="text-xs text-slate-500 hover:text-slate-300 flex items-center gap-1"
      >
        <span>{open ? '\u25BC' : '\u25B6'}</span>
        {title}
      </button>
      {open && (
        <pre className="mt-1 text-xs text-slate-400 bg-slate-950 border border-slate-800 rounded p-3 overflow-x-auto max-h-96 overflow-y-auto">
          {JSON.stringify(data, null, 2)}
        </pre>
      )}
    </div>
  );
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes}B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)}KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)}MB`;
}

function tryParse(s: string): any {
  try {
    return JSON.parse(s);
  } catch {
    return s;
  }
}
